import { gql } from '@apollo/client';
import { Button, Flex, Switch, Text, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Shield } from '~auth/Shield';
import { AssignUsersModal } from '~components/organization/EmailNotification/assignUserModel';
import { EmailNotificationTable } from '~components/organization/EmailNotification/EmailNotificationTable';
import { ResetNotificationModal } from '~components/organization/EmailNotification/ResetNotificationModal';
import {
  FlattenedNotificationRow,
  useEmailNotificationTable,
} from '~components/organization/EmailNotification/useEmailNotification';
import { InfoAlert, WarningAlert } from '~components/ui/Alert';
import { BackButton } from '~components/ui/BackButton';
import { AssignEmailIcon, ResetIcon } from '~components/ui/icons';
import { Page } from '~components/ui/Page';
import { PageActions } from '~components/ui/PageActions';
import { PageContentZte } from '~components/ui/PageContentZte';
import { PageHeading } from '~components/ui/PageHeading';
import { PageLoader } from '~components/ui/PageLoader';
import { NotificationStatus, NotificationType } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { fromError } from '~utils/errors';
import { ensure, isDefined } from '~utils/types';
import {
  useEmailNotificationsPageQuery,
  useUpdateNotificationSettingsMutation,
  useUpdateNotificationStatusMutation,
} from './__generated__/emailNotification.graphql';

export function OrganizationCustomersEmailNotification() {
  const toast = useToast();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const navigate = useNavigate();
  const { customerId } = useParams();
  const { data, loading, error } = useEmailNotificationsPageQuery({
    variables: {
      customerId: ensure(customerId),
    },
  });
  const emailAddresses = useMemo(
    () => data?.customer?.notification?.emailAddresses ?? [],
    [data?.customer?.notification?.emailAddresses],
  );
  const customerName = data?.customer?.name;
  const [status, setStatus] = useState<NotificationStatus>(
    data?.customer?.notification?.status ?? NotificationStatus.Disabled,
  );
  useEffect(() => {
    if (data?.customer?.notification?.status) {
      setStatus(data.customer.notification.status);
    }
  }, [data?.customer?.notification?.status]);
  const [updateNotificationSettings] = useUpdateNotificationSettingsMutation({
    refetchQueries: ['EmailNotificationsPage'],
    awaitRefetchQueries: true,
  });

  const [updateNotificationStatus] = useUpdateNotificationStatusMutation({
    refetchQueries: ['EmailNotificationsPage'],
    awaitRefetchQueries: true,
  });
  const handleUpdate = useCallback(
    async (updatedSettings: FlattenedNotificationRow[]) => {
      try {
        const { data: result } = await updateNotificationSettings({
          variables: {
            input: {
              customerId: ensure(customerId),
              status,
              ...(updatedSettings.length > 0 && {
                settings: updatedSettings.map((s) => ({
                  notificationType: s.type as NotificationType,
                  alertType: s.trigger,
                  reminderType: s.frequency,
                  status: s.status,
                })),
              }),
            },
          },
        });

        if (!result?.notificationSettingsUpdate) {
          throw new Error('notificationUpdateFailed');
        }
      } catch (err) {
        toast({
          status: 'error',
          title: t('updateNotificationError'),
          description: fromError(err, 'NotificationSettingsUpdate'),
        });
      }
    },
    [updateNotificationSettings, customerId, status, toast],
  );

  const handleStatusUpdate = useCallback(
    async (status: NotificationStatus) => {
      try {
        const { data: result } = await updateNotificationStatus({
          variables: {
            input: {
              customerId: ensure(customerId),
              status,
            },
          },
        });
        if (!result?.notificationStatusUpdate) {
          throw new Error('notificationUpdateFailed');
        }
        const isEnabled = result?.notificationStatusUpdate.status === NotificationStatus.Enabled;
        toast({
          status: isEnabled ? 'success' : 'info',
          title: isEnabled
            ? t('emailNotificationsEnabled', { customerName: customerName })
            : t('emailNotificationsDisabled', { customerName: customerName }),
          description: (
            <Text>
              <Trans
                i18nKey={
                  isEnabled ? 'emailNotificationsEnabledDesc' : 'emailNotificationsDisabledDesc'
                }
                values={{ customerName: customerName }}
                components={{ bold: <Text as="span" fontWeight="bold" /> }}
              />
            </Text>
          ),
          duration: 4000,
          isClosable: true,
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('updateNotificationError'),
          description: fromError(err, 'NotificationSettingsUpdate'),
        });
      }
    },
    [updateNotificationStatus, customerId, toast, customerName],
  );

  useEffect(() => {
    if (!loading && !error && data?.customer && data.customer.notification?.settings == null) {
      handleUpdate([]);
    }
  }, [data, loading, error, handleUpdate, status]);

  const table = useEmailNotificationTable({
    settings:
      data?.customer?.notification?.settings?.map((s) => ({
        type: s.notificationType,
        status: s.status,
        trigger: s.alertType,
        frequency: s.reminderType,
      })) || [],
    onUpdate: handleUpdate,
    status: data?.customer?.notification?.status || NotificationStatus.Disabled,
    email: emailAddresses.length > 0,
  });

  const handleGoBack = () => {
    navigate('../customers');
  };
  function handleAddRecipients() {
    setIsAssignModalOpen(true);
  }

  function handleResetClicked() {
    setIsResetModalOpen(true);
  }

  const warningassignRecipients = (
    <Flex
      align="center"
      border="1px solid"
      borderColor="orange.300"
      borderRadius="md"
      px={3}
      py={1}
      color="orange.600"
      cursor="pointer"
      _hover={{ bg: 'orange.50' }}
      onClick={handleAddRecipients}
    >
      <AssignEmailIcon width="16px" height="16px" color="orange.600" />
      <Text ml={2} fontWeight="medium">
        {t('manageRecipients')}
      </Text>
    </Flex>
  );
  return (
    <Page title={t('EmailNotification')} pageName="emailNotification_overview">
      <PageContentZte>
        <PageHeading
          floatingButton={<BackButton onClick={handleGoBack} />}
          description={data?.customer?.name}
        >
          {t('emailNotification')}
        </PageHeading>
      </PageContentZte>
      <PageContentZte>
        <PageActions
          left={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Text fontSize="2xl" fontWeight="bold">
                {t('allNotifications')}
              </Text>
              <Switch
                size="lg"
                isChecked={data?.customer?.notification?.status === NotificationStatus.Enabled}
                onChange={(e) => {
                  const newStatus = e.target.checked
                    ? NotificationStatus.Enabled
                    : NotificationStatus.Disabled;
                  setStatus(newStatus);
                  handleStatusUpdate(newStatus);
                }}
              />
            </div>
          }
          right={
            <Shield requiredPermissions={[Permission.CustomerUpdate]}>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={handleResetClicked}
                leftIcon={<ResetIcon width="5" height="5" />}
                isDisabled={data?.customer?.notification?.status === NotificationStatus.Disabled}
              >
                {t('reset')}
              </Button>
              <Button
                variant="solid"
                colorScheme="blue"
                onClick={handleAddRecipients}
                style={{ marginLeft: '32px' }}
              >
                {t('manageRecipients')}
              </Button>
            </Shield>
          }
        />
        {emailAddresses.length === 0 && (
          <WarningAlert
            style={{ marginBottom: '10px' }}
            actionButton={warningassignRecipients}
            disabled={data?.customer?.notification?.status !== NotificationStatus.Enabled}
          >
            {t('encWarning')}
          </WarningAlert>
        )}
        <InfoAlert
          style={{ marginBottom: '32px' }}
          disabled={data?.customer?.notification?.status !== NotificationStatus.Enabled}
        >
          <Trans i18nKey="assignUsersInfo" components={{ strong: <strong /> }} />
        </InfoAlert>
        {loading ? (
          <PageLoader />
        ) : error ? (
          <HandleApiError error={error} />
        ) : (
          isDefined(data?.customer) && <EmailNotificationTable table={table} />
        )}
      </PageContentZte>
      <AssignUsersModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        id={data?.customer?.notification?.id || ''}
        customerName={data?.customer?.name || ''}
        emailAddresses={emailAddresses}
      />
      <ResetNotificationModal
        isOpen={isResetModalOpen}
        onCancel={() => setIsResetModalOpen(false)}
        status={status}
      />
    </Page>
  );
}

OrganizationCustomersEmailNotification.graphql = {
  queries: {
    EmailNotificationsPage: gql`
      query EmailNotificationsPage($customerId: ID!) {
        customer(id: $customerId) {
          id
          name
          notification {
            id
            emailAddresses
            status
            customerId
            ...EmailNotificationTable
          }
        }
      }
    `,
  },
  mutations: {
    updateNotificationSettings: gql`
      mutation updateNotificationSettings($input: NotificationInput!) {
        notificationSettingsUpdate(input: $input) {
          id
        }
      }
    `,
    updateNotificationStatus: gql`
      mutation updateNotificationStatus($input: NotificationStatusInput!) {
        notificationStatusUpdate(input: $input) {
          id
          status
        }
      }
    `,
  },
};
